import React, { useState, useEffect } from "react";
import { Button, Table, Tag, Modal, Form, Input, Select, Dropdown } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PageHeader from "../../../components/header/PageHeader";
import api from "../../../api";
import { IconArrowRight } from "@tabler/icons-react";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import { useToast } from "../../../components/toast";

const TYPE_OPTIONS = [
  { label: "Ангилал", value: "CATEGORY" },
  { label: "Таг", value: "TAG" },
];

const TYPE_COLOR = { CATEGORY: "blue", TAG: "green" };

export default function NewsTaxonomies() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [form] = Form.useForm();
  const toast = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.news_taxonomies_list();
      setData(res.data.data || []);
    } catch {
      toast("Өгөгдөл татахад алдаа гарлаа", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditItem(record);
    form.setFieldsValue({
      name: record.name,
      slug: record.slug,
      type: record.type,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      if (editItem) {
        await api.news_taxonomies_update(editItem.id, { ...values });
      } else {
        await api.news_taxonomies_create({ ...values });
      }

      toast(
        editItem ? "Амжилттай засагдлаа" : "Амжилттай нэмэгдлээ",
        "success",
      );
      setModalOpen(false);
      fetchData();
    } catch {
      toast("Алдаа гарлаа", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      await api.news_taxonomies_delete(deleteModal.id);
      toast("Амжилттай устгагдлаа", "success");
      setDeleteModal({ open: false, id: null });
      fetchData();
    } catch {
      toast("Устгахад алдаа гарлаа", "error");
    }
  };

  const handleNameChange = (e) => {
    if (editItem) return;
    const slug = e.target.value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    form.setFieldValue("slug", slug);
  };

  const columns = [
    {
      title: "Төрөл",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={TYPE_COLOR[type]}>
          {type === "CATEGORY" ? "Ангилал" : "Таг"}
        </Tag>
      ),
      filters: [
        { text: "Ангилал", value: "CATEGORY" },
        { text: "Таг", value: "TAG" },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {name}
        </span>
      ),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code>,
    },

    {
      title: "Мэдээний тоо",
      dataIndex: "_count",
      key: "newsCount",
      render: (count) => count?.news ?? 0,
      sorter: (a, b) => (a._count?.news ?? 0) - (b._count?.news ?? 0),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete")
                setDeleteModal({ open: true, id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="news-section">
      <PageHeader
        title="Мэдээний ангилал & таг"
        sub="Мэдээний ангилал, тагийн жагсаалт"
        rSide={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Нэмэх
          </Button>
        }
      />

      <div className="table-wrap">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={{ spinning: loading, size: "small" }}
          pagination={{ pageSize: 20 }}
        />
      </div>

      <Modal
        title={editItem ? "Засах" : "Шинэ ангилал / Таг"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={editItem ? "Хадгалах" : "Нэмэх"}
        confirmLoading={submitLoading}
        cancelText="Болих"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Төрөл"
            name="type"
            rules={[{ required: true, message: "Төрөл сонгоно уу" }]}
          >
            <Select options={TYPE_OPTIONS} placeholder="Сонгох" />
          </Form.Item>

          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу" }]}
          >
            <Input onChange={handleNameChange} placeholder="жш: Эдийн засаг" />
          </Form.Item>

          <Form.Item
            label="Slug"
            name="slug"
            rules={[{ required: true, message: "Slug оруулна уу" }]}
          >
            <Input placeholder="жш: ediin-zasag" />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        title="Устгах уу?"
        description="Энэ ангилал/тагийг устгахад холбоотой мэдээнүүдээс салгагдана."
        variant="warning"
      />
    </section>
  );
}
